import React from 'react';
import { Heart, Users, Globe, BookOpen, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Heart,
      title: t('about.worship'),
      description: t('about.worshipText'),
      color: 'from-rose-500 to-pink-600',
    },
    {
      icon: Users,
      title: t('about.community'),
      description: t('about.communityText'),
      color: 'from-sky-500 to-blue-600',
    },
    {
      icon: BookOpen,
      title: t('about.teaching'),
      description: t('about.teachingText'),
      color: 'from-amber-500 to-orange-600',
    },
    {
      icon: Globe,
      title: t('about.service'),
      description: t('about.serviceText'),
      color: 'from-emerald-500 to-teal-600',
    },
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="about-title">
            {t('about.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#0f2440] to-sky-500 mx-auto mb-6"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20 max-w-6xl mx-auto">
          {/* Image */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0f2440] to-sky-600 rounded-3xl transform rotate-3"></div>
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_19aead6b-8655-4d59-bd82-6868e26d577e/artifacts/d627051975e093c2_image.png"
              alt="Unser Pastor"
              className="relative rounded-3xl shadow-2xl w-full h-[450px] object-cover transform -rotate-3 hover:rotate-0 transition-transform duration-500"
              data-testid="about-image"
            />
          </div>

          {/* Text Content */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              {t('about.familyTitle')}
            </h3>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t('about.desc')}
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t('about.missionText')}
            </p>
            <div className="pt-4">
              <button
                onClick={() => document.getElementById('visit')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="about-visit-btn"
                className="px-8 py-3 bg-[#0f2440] text-white rounded-lg font-semibold 
                           hover:bg-[#1a3a5c] transition-all hover:scale-105 hover:shadow-lg
                           inline-flex items-center gap-2"
              >
                {t('about.visitBtn')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {values.map((value, index) => (
            <div
              key={index}
              data-testid={`about-value-${index}`}
              className="group bg-white p-7 rounded-2xl shadow-sm border border-gray-100 
                         hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${value.color} 
                              rounded-2xl flex items-center justify-center mb-5 
                              group-hover:scale-110 transition-transform`}>
                <value.icon className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                {value.title}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="mt-16 bg-[#0f2440] rounded-3xl p-10 md:p-12 text-center max-w-5xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-5">
            {t('about.mission')}
          </h3>
          <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t('about.missionText')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;
